/**
 * POS-ORDER-NUMBER.1 — how a movement's source document is named on screen.
 *
 * A movement points at whatever produced it, and until POS-ORDER-NUMBER.1 the
 * only thing it carried was an id. For a POS order that id is an ObjectId, so
 * every screen showing a sale showed something like "…3a5bf652" — the identity,
 * and recognisable to almost nobody. The number is what the till prints, what
 * the guest receipt says, and what someone reconciling a movement against an
 * order actually has in front of them.
 *
 * Prefer the number; fall back to the id. The fallback is not an edge case —
 * it is most rows:
 *
 *   - everything posted before the sender started including a number, which is
 *     the whole of history up to 2026-09-10
 *   - every source type that has no human number of its own and never will: a
 *     stock count, a transfer, a goods receipt, a purchase order are all
 *     ObjectId-identified
 *
 * So an absent number must render the id rather than an empty cell. Empty reads
 * as "no source"; the row does have one.
 *
 * The id is never dropped. It goes in the tooltip, because it is the identity —
 * the number is unique only per COMPANY, so two venues in one company share
 * order numbers routinely. Match and link on the id, read the number.
 */

const OBJECTID_RE = /^[a-f0-9]{24}$/i;

export interface SourceRef {
  /** What to show. */
  text: string;
  /** What to put in the tooltip: the identity, in full. */
  full: string;
}

export const formatSourceRef = (
  id: string | null | undefined,
  number?: string | null,
): SourceRef | null => {
  if (number) {
    return { text: number, full: id ? `Order ${number} · ${id}` : `Order ${number}` };
  }
  if (!id) return null;
  // A non-ObjectId id is already something a person can read — an external POS
  // ref, a manual reference — so it is shown whole. Only an ObjectId is
  // shortened, and only because its first 16 characters carry nothing.
  return OBJECTID_RE.test(id) ? { text: `…${id.slice(-8)}`, full: id } : { text: id, full: id };
};
